import React, { useEffect, useState } from "react";
import { Form, Button, Table, Radio, Image } from "antd";
import Banner from "../../../../views/components/sections/Banner";
import { getSingleImageUrl } from "../../../../lib/utils";
import TabsContain from "../../../../views/components/reuseComp/TabsContain";
import { useMessageApi } from "../../../../provider/AntdNotification";
import { useQueryState } from "nuqs";
import {
  useServiceDetail,
  useServiceList,
  useServiceSave,
} from "../../../../hooks/api/service/hooks";
import { IServicePayload, ServiceItem } from "../../../../types/api/service";
import ServiceOverview from "./serviceOverview";
import ExecutiveProcess from "./executiveProcessSection";
import RelevantIndustries from "./relevantIndustries";
import WhatMakesUsDifferent from "./whatMakesUsDefferent";
import OurProcess from "./ourProcess";
import WhatWeSolve from "./whatWeSolve";

interface Props {
  allowURLStateUpdate?: boolean;
  setBreadcrumb: React.Dispatch<React.SetStateAction<string>>;
}

const ServiceDetailTab: React.FC<Props> = ({
  allowURLStateUpdate = true,
  setBreadcrumb,
}) => {
  const DEFAULT_NUQS_CONFIG = {
    throttleMs: !allowURLStateUpdate ? Infinity : 0,
  };

  const [form] = Form.useForm();
  const message = useMessageApi();
  const [tab, setTab] = useQueryState("tab");
  const activeTab = tab || "Banner";

  const { data, isFetching: isListFetching } = useServiceList({
    page: 1,
    limit: 20,
    ...DEFAULT_NUQS_CONFIG,
  });

  const serviceList: ServiceItem[] = data?.data || [];

  const [selectedSlug, setSelectedSlug] = useState<string>("");
  const [selectedService, setSelectedService] =
    useState<IServicePayload | null>(null);
  const [isSelectionDone, setIsSelectionDone] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [reviewMode, setReviewMode] = useState<"list" | "add" | "edit">("list");
  const [editingReviewIndex, setEditingReviewIndex] = useState<number | null>(
    null,
  );

  const qData = data?.qData ?? { page: 1, limit: 20 };

  const { data: serviceDetail, isFetching: isDetailFetching } =
    useServiceDetail(selectedSlug);

  const { mutate: saveService, isPending } = useServiceSave({ qData });

  useEffect(() => {
    if (serviceDetail?.data) {
      setSelectedService(serviceDetail.data);
    }
  }, [serviceDetail]);

  const handleChangeTab = (section: string) => {
    setTab(section);
    setIsEditing(false);
    setBreadcrumb(section);
  };

  const handleNext = () => {
    if (!selectedSlug) {
      return message.warning("Please select a service first");
    }
    setIsSelectionDone(true);
  };

  const handleCancel = () => {
    setIsEditing(false);
  };

  const handleSave = async () => {
    if (!selectedService) return;

    try {
      const values = await form.validateFields();
      let payload: IServicePayload = { ...selectedService };

      if (activeTab === "Banner") {
        payload.banner = {
          ...selectedService.banner,
          ...values.banner,
          image:
            getSingleImageUrl(values?.banner?.image) ||
            selectedService.banner?.image,
        };
      }

      if (activeTab === "Overview") {
        payload.overview = {
          ...selectedService.overview,
          ...values.overview,
          image:
            getSingleImageUrl(values?.overview?.image) ||
            selectedService.overview?.image,
        };
      }
      if (activeTab === "Executive Search") {
        const ep = values.executiveProcess || {};

        payload.executiveProcess = {
          heading:
            ep.heading ?? selectedService.executiveProcess?.heading ?? "",

          subtext:
            ep.subtext ?? selectedService.executiveProcess?.subtext ?? "",

          points: ep.points ?? selectedService.executiveProcess?.points ?? [],
        };
      }

      if (activeTab == "What Makes Us Different") {
        payload.whatMakesUsDifferent = {
          heading:
            values.whatMakesUsDifferent.heading ??
            selectedService.whatMakesUsDifferent?.heading,
          subtext:
            values.whatMakesUsDifferent.subtext ??
            selectedService.whatMakesUsDifferent?.subtext,
          image:
            getSingleImageUrl(values?.whatMakesUsDifferent?.image) ??
            selectedService.whatMakesUsDifferent?.image,
          points:
            values.whatMakesUsDifferent.points ??
            selectedService.whatMakesUsDifferent?.points ??
            [],
        };
      }
      if (activeTab == "Relevant Industries") {
        payload.relevantIndustries = {
          heading:
            values.relevantIndustries.heading ??
            selectedService.relevantIndustries?.heading,
          subtext:
            values.relevantIndustries.subtext ??
            selectedService.relevantIndustries?.subtext,
          points:
            values.relevantIndustries.points ??
            selectedService.relevantIndustries?.points ??
            [],
        };
      }
      if (activeTab === "Our Process") {
        const existingProcess = selectedService.ourProcess;
        const existingContent = existingProcess?.content ?? [];
        let updatedContent = [...existingContent];

        if (reviewMode === "add")
          updatedContent = [...existingContent, ...values.ourProcess.content];
        if (reviewMode === "edit" && editingReviewIndex !== null)
          updatedContent[editingReviewIndex] = values.ourProcess.content[0];

        payload.ourProcess = {
          heading: values.ourProcess.heading ?? existingProcess?.heading ?? "",
          subtext: values.ourProcess.subtext ?? existingProcess?.subtext ?? "",
          content: updatedContent,
        };
      }
      if (activeTab === "What We Solve") {
        const existingProcess = selectedService.whatWeSolve;
        const existingSections = existingProcess?.sections ?? [];
        let updatedSections = [...existingSections];

        if (reviewMode === "add")
          updatedSections = [
            ...existingSections,
            ...values.whatWeSolve.sections,
          ];
        if (reviewMode === "edit" && editingReviewIndex !== null)
          updatedSections[editingReviewIndex] = values.whatWeSolve.sections[0];

        payload.whatWeSolve = {
          heading: values.whatWeSolve.heading ?? existingProcess?.heading ?? "",
          subtext: values.whatWeSolve.subtext ?? existingProcess?.subtext ?? "",
          sections: updatedSections,
        };
      }
      saveService(payload, {
        onSuccess: () => {
          message.success("Saved successfully");
          setSelectedService(payload);
          setReviewMode("list");
          setIsEditing(false);
          form.resetFields();
        },
        onError: () => message.error("Failed to save"),
      });
    } catch (err) {
      console.log(err);
    }
  };

  type SectionField = "Our Process" | "What We Solve";
  const handleDelete = (index: number, field: SectionField) => {
    if (!selectedService) return;
    let payload: IServicePayload = { ...selectedService };

    if (field === "Our Process") {
      const currentProcess = selectedService.ourProcess;
      if (!currentProcess) return;
      const updatedContent = currentProcess.content.filter(
        (_, i) => i !== index,
      );
      payload = {
        ...selectedService,
        ourProcess: { ...currentProcess, content: updatedContent },
      };
    }

    if (field === "What We Solve") {
      const currentExpertise = selectedService.whatWeSolve;
      if (!currentExpertise) return;
      const updatedContent = currentExpertise.sections.filter(
        (_, i) => i !== index,
      );
      payload = {
        ...selectedService,
        whatWeSolve: { ...currentExpertise, sections: updatedContent },
      };
    }

    console.log(serviceList);

    saveService(payload, {
      onSuccess: () => {
        message.success("Data deleted successfully");
        setSelectedService(payload);
        setReviewMode("list");
        setEditingReviewIndex(null);
      },
      onError: () => message.error("Failed to delete"),
    });
  };

  const columns = [
    {
      title: "",
      align: "center" as const,
      render: (_: any, record: ServiceItem) => (
        <Radio
          checked={selectedSlug === record.slug}
          onChange={() => setSelectedSlug(record.slug)}
        />
      ),
    },
    {
      title: "Image",
      dataIndex: "image",
      align: "center" as const,
      render: (v: string) => <Image src={v} height={60} />,
    },
    {
      title: "Name",
      dataIndex: "heading",
      render: (text: string) => <span className="font-medium">{text}</span>,
    },
    {
      title: "",
      align: "center" as const,
      render: (_: any, record: ServiceItem) =>
        selectedSlug === record.slug ? (
          <Button
            type="dashed"
            className="bg-primary text-white px-9"
            onClick={(e) => {
              e.stopPropagation();
              handleNext();
            }}
          >
            Next
          </Button>
        ) : null,
    },
  ];

  const sections = [
    "Banner",
    "Overview",
    "Executive Search",
    "What We Solve",
    "Our Process",
    "What Makes Us Different",
    "Relevant Industries",
  ];

  return (
    <div>
      {!isSelectionDone && (
        <div className="p-4 rounded-md shadow-sm">
          <h2 className="text-lg font-semibold mb-3">
            Select a Service to Edit
          </h2>

          <Table
            dataSource={serviceList}
            columns={columns}
            rowKey="_id"
            pagination={false}
            bordered
            scroll={{ x: "max-content" }}
            className="custom-table-class"
            loading={isListFetching || isDetailFetching}
            onRow={(record: ServiceItem) => ({
              onClick: () => setSelectedSlug(record.slug),
            })}
            rowClassName={(record: ServiceItem) =>
              selectedSlug === record.slug
                ? "hover:bg-gar-100 cursor-pointer transition-all"
                : "hover:bg-gray-50 cursor-pointer"
            }
          />
        </div>
      )}

      {isSelectionDone && selectedService && (
        <>
          <div className="flex mt-4 w-full">
            <Button
              type="dashed"
              className="mb-4 bg-primary text-white w-full"
              onClick={() => {
                setIsSelectionDone(false);
                setSelectedSlug("");
              }}
            >
              Select Another Service
            </Button>
          </div>

          <h2 className="text-lg font-semibold mb-4">
            Editing Service:{" "}
            <span className="text-primary">
              {selectedService.banner.heading}
            </span>
          </h2>

          <TabsContain
            sections={sections}
            activeTab={activeTab}
            handleChangeTab={handleChangeTab}
          />

          {activeTab === "Banner" && (
            <Banner
              isEditing={isEditing}
              setIsEditing={setIsEditing}
              handleCancel={handleCancel}
              handleSave={handleSave}
              form={form}
              bannerData={selectedService?.banner}
              loading={isPending}
            />
          )}
          {activeTab === "Overview" && (
            <ServiceOverview
              isEditing={isEditing}
              setIsEditing={setIsEditing}
              handleCancel={handleCancel}
              handleSave={handleSave}
              form={form}
              overviewData={selectedService?.overview}
              loading={isPending}
            />
          )}
          {activeTab === "Executive Search" && (
            <ExecutiveProcess
              isEditing={isEditing}
              setIsEditing={setIsEditing}
              handleCancel={handleCancel}
              handleSave={handleSave}
              form={form}
              executiveData={selectedService?.executiveProcess}
              loading={isPending}
            />
          )}
          {activeTab === "What We Solve" && (
            <WhatWeSolve
              isEditing={isEditing}
              setIsEditing={setIsEditing}
              handleCancel={handleCancel}
              handleSave={handleSave}
              form={form}
              whatWeSolveData={selectedService?.whatWeSolve}
              loading={isPending}
              onDelete={handleDelete}
              mode={reviewMode}
              setMode={setReviewMode}
              editingIndex={editingReviewIndex}
              setEditingIndex={setEditingReviewIndex}
            />
          )}
          {activeTab === "Our Process" && (
            <OurProcess
              isEditing={isEditing}
              setIsEditing={setIsEditing}
              handleCancel={handleCancel}
              handleSave={handleSave}
              form={form}
              ourProcessData={selectedService?.ourProcess}
              loading={isPending}
              onDelete={handleDelete}
              mode={reviewMode}
              setMode={setReviewMode}
              editingIndex={editingReviewIndex}
              setEditingIndex={setEditingReviewIndex}
            />
          )}
          {activeTab === "What Makes Us Different" && (
            <WhatMakesUsDifferent
              isEditing={isEditing}
              setIsEditing={setIsEditing}
              handleCancel={handleCancel}
              handleSave={handleSave}
              form={form}
              whatMakesUsDifferentData={selectedService?.whatMakesUsDifferent}
              loading={isPending}
            />
          )}
          {activeTab === "Relevant Industries" && (
            <RelevantIndustries
              isEditing={isEditing}
              setIsEditing={setIsEditing}
              handleCancel={handleCancel}
              handleSave={handleSave}
              form={form}
              relevantIndustriesdata={selectedService?.relevantIndustries}
              loading={isPending}
            />
          )}
        </>
      )}
    </div>
  );
};

export default ServiceDetailTab;
